/**
 * TaskReferenceView — React NodeView for the TaskReference inline node.
 *
 * Renders a clickable checkbox + task title as a real interactive element
 * instead of a CSS pseudo-element, making the checkbox clickable. The title
 * is a separate click target that opens the entity preview modal (see
 * EntityPreviewContext) instead of toggling done state.
 */

import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { apiPatch } from '../../lib/api';
import { useEntityPreview } from '../../contexts/EntityPreviewContext';

export function TaskReferenceView({ node, updateAttributes, extension }: NodeViewProps) {
  const { id, title, checked } = node.attrs;
  const { openPreview } = useEntityPreview();

  async function handleToggle(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    const newChecked = !checked;
    const newStatus = newChecked ? 'done' : 'todo';

    // Optimistic update
    updateAttributes({ checked: newChecked });

    try {
      await apiPatch(`/tasks/${id}`, { status: newStatus });
      extension.options.onTaskStatusChange?.(id, newStatus);
    } catch (err) {
      console.error('Failed to update task status:', err);
      // Revert on error
      updateAttributes({ checked: !newChecked });
    }
  }

  function handleOpen(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (id) {
      openPreview('task', id);
    }
  }

  return (
    <NodeViewWrapper as="span" className={`task-reference-node ${checked ? 'task-reference-checked' : ''}`} data-checked={String(!!checked)}>
      <button
        type="button"
        className={`task-reference-checkbox ${checked ? 'task-reference-checkbox-checked' : ''}`}
        onClick={handleToggle}
        contentEditable={false}
        aria-label={checked ? 'Mark as incomplete' : 'Mark as complete'}
      />
      <span
        className={`task-reference-title ${checked ? 'task-reference-title-checked' : ''}`}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
      >
        {title || 'Untitled task'}
      </span>
    </NodeViewWrapper>
  );
}
